/**
 * WS-13 Feature 5 — BEFORE/AFTER PHOTOS: the ONE thing the generated client can't express.
 *
 * Every metadata call (list / init / confirm / patch / consent / delete) now goes through the generated,
 * OpenAPI-typed `api` client (lib/api.ts) from data/photos.ts — the /v1/clients/:clientId/photos and
 * /v1/photos/:id* paths are in the regenerated contract, so there is no longer a hand-rolled fetch seam.
 *
 * The image BYTES, however, are NEVER POSTed through our API: a presigned PUT (or POST-policy) sends them
 * straight to the S3-compatible bucket — cross-origin to the bucket, NO session cookie (the presigned URL IS
 * the capability). openapi-fetch only speaks our own contract, so this raw uploader stays hand-rolled. Our
 * API only ever sees the METADATA.
 */
import type { PhotoUploadTicket } from '@salonomy/types';
import { ApiClientError } from './api.js';

/**
 * PUT the raw image bytes straight to the presigned bucket URL — NOT through our API, NO session cookie
 * (the presigned URL is the capability). `fields` (when present) means a POST-policy upload: we build a
 * multipart form with the policy fields + the file. Otherwise it's a simple PUT of the bytes.
 */
export async function uploadToPresignedUrl(ticket: PhotoUploadTicket, file: File): Promise<void> {
  let res: Response;
  if (ticket.fields && Object.keys(ticket.fields).length > 0) {
    const form = new FormData();
    for (const [k, v] of Object.entries(ticket.fields)) form.append(k, v);
    form.append('Content-Type', file.type);
    form.append('file', file);
    res = await fetch(ticket.uploadUrl, { method: 'POST', body: form });
  } else {
    res = await fetch(ticket.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': file.type },
      body: file,
    });
  }
  if (!res.ok) {
    throw new ApiClientError(
      res.status,
      undefined,
      `Upload to storage failed (${res.status}) — the bytes did not land.`,
    );
  }
}
